import { useEffect, useState } from 'react';
import { useSesion } from '../lib/sesion';
import { ApiError } from '../lib/api';
import { REGLAS } from '../lib/clave';
import Aviso from './Aviso';

/**
 * «MI CLAVE» Y EL PORTÓN — la misma pantalla con dos pieles.
 *
 * ── LO QUE ES CADA UNA ──────────────────────────────────────────────────────
 *  · `<CambiarClave abierto onCerrar />` es **Mi clave**: la ventanita del botón
 *    de la cabecera, para cambiarla cuando uno quiera. Pedida el 2026-08-26.
 *  · `<CambiarClave porton />` es **el PORTÓN**: la pantalla que tapa el portal
 *    entero mientras el usuario siga con la clave que se le entregó. Pedido el
 *    2026-08-10 («cuando un usuario entre por primera vez con la clave
 *    predeterminada automaticamente debe salir la pantalla para que cambie la
 *    contraseña por una personalizada»), construido entonces en la rama
 *    `feat/porton-de-contrasena` (PR #2, nunca mezclado) y **puesto en marcha el
 *    2026-08-26**, cuando lo volvió a pedir: «que cada usuario cuando ingrese a
 *    su cuenta el portal le solicite el cambio de clave».
 *
 * Son un solo componente y no dos archivos a propósito: el formulario, la
 * política, las validaciones y el manejo de errores son EXACTAMENTE los mismos,
 * y dos copias del mismo formulario divergen —una acaba pidiendo algo que la
 * otra no, y el usuario que pasó por las dos no entiende por qué—. Lo que cambia
 * es el envoltorio y las salidas, que es justo lo que `porton` decide.
 *
 * ── POR QUÉ EL PORTÓN NO ES UNA RUTA ────────────────────────────────────────
 * No está en `rutas.tsx`, no tiene URL y no existe como fila en `auth.pagina`.
 * Lo devuelve `Marco.tsx` con un return temprano, ANTES de pintar la cabecera y
 * el lateral. Una ruta dentro de `<Routes>` no serviría: `Marco` pinta el marco
 * ALREDEDOR de `children`, así que el usuario vería el menú completo por encima
 * del portón y bastaría teclear otra URL para seguir navegando.
 *
 * Y no es solo cosmético: con el portón puesto, el servidor ya devuelve 403 en
 * los 51 endpoints (`requiereSesion`, `_lib/auth.ts`). Si alguien quitara el
 * return de `Marco`, el portal se vería entero y VACÍO, no abierto.
 *
 * ── POR QUÉ EL PORTÓN TRAE SU PROPIO «SALIR» ────────────────────────────────
 * Los dos botones de cerrar sesión de la aplicación viven en `Marco`, y el
 * return temprano que muestra esta pantalla se los lleva por delante. Sin el
 * botón de aquí, quien no recuerde la clave que le dieron queda encerrado: la
 * cookie es `httpOnly`, así que no puede ni borrarla desde el navegador.
 *
 * ── LA POLÍTICA NO SE ESCRIBE AQUÍ ──────────────────────────────────────────
 * Sale de `src/lib/clave.ts`, el MISMO archivo que importa el endpoint. Esta
 * pantalla lo usa para dos cosas: pintar la lista de requisitos que se va
 * poniendo en verde mientras el usuario teclea, y no gastar un viaje al servidor
 * con una clave que ya se sabe que no vale. **El servidor manda**, y su error se
 * pinta TAL CUAL —los textos de las reglas son los mismos en los dos lados, así
 * que no hay forma de que digan cosas distintas—.
 *
 * La lista de requisitos VISIBLE es lo que la hace usable: sin ella, la única
 * forma de saber por qué no sirve una clave es que te la rechacen. Y por eso la
 * regla «no contiene tu nombre de usuario» se da por cumplida con el campo
 * vacío: un requisito en rojo antes de escribir nada se lee como un error.
 */

export default function CambiarClave({ abierto = false, onCerrar, porton = false }: {
  abierto?: boolean;
  onCerrar?: () => void;
  /** Piel PORTÓN: pantalla completa, sin cerrar, con su propio Salir. */
  porton?: boolean;
}) {
  const { sesion, cambiarClave, salir } = useSesion();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetir, setRepetir] = useState('');
  const [ver, setVer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  /* El portón está siempre visible: quien lo muestra es `Marco`, que deja de
   * pintarlo solo cuando la sesión ya no tiene el cambio pendiente. */
  const visible = porton || abierto;

  /* Al abrir se limpia todo: una clave escrita a medias no puede quedar viva en
   * el estado de un componente que sigue montado. */
  useEffect(() => {
    if (!visible) return;
    setActual(''); setNueva(''); setRepetir('');
    setError(null); setListo(false); setVer(false);
  }, [visible]);

  /* Tras el cambio la ventanita se cierra sola. El saludo del recorrido llega
   * justo después (`Recorrido.tsx` espera un poco más a propósito), así que no
   * hace falta que el usuario cierre nada a mano.
   *
   * En el PORTÓN no hay nada que cerrar y `onCerrar` no existe: la sesión pasa a
   * tener la clave personalizada y `Marco` deja de pintar esta pantalla por sí
   * solo, igual que el login. La guarda de `onCerrar` no es defensiva de más —
   * sin ella, el temporizador llamaría a `undefined`. */
  useEffect(() => {
    if (!listo || !onCerrar) return;
    const t = window.setTimeout(onCerrar, 1400);
    return () => window.clearTimeout(t);
  }, [listo, onCerrar]);

  /* Escape cierra la ventanita. En el portón NO se escucha: no hay salida por
   * ahí, y una tecla que parece cerrar y no cierra es peor que ninguna. */
  useEffect(() => {
    if (!abierto || porton || !onCerrar) return;
    const alTeclear = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto, porton, onCerrar]);

  if (!visible || !sesion) return null;

  const suplantando = !!sesion.suplantacion;

  /* Las reglas de la política que la clave escrita todavía no cumple. */
  const faltan = REGLAS.filter((r) => !r.cumple(nueva, sesion.usuario.username));

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!actual) { setError('Escribe tu clave actual.'); return; }
    if (faltan.length) {
      setError('La clave nueva todavía no cumple los requisitos de abajo.');
      return;
    }
    if (nueva === actual) {
      setError('La clave nueva tiene que ser distinta de la actual.');
      return;
    }
    if (nueva !== repetir) { setError('Las dos claves nuevas no coinciden.'); return; }
    setError(null);
    setEnviando(true);
    try {
      await cambiarClave(actual, nueva);
      setActual(''); setNueva(''); setRepetir('');
      setListo(true);
    } catch (err) {
      /* UNA SESIÓN CADUCADA CON EL PORTÓN PUESTO dejaría esta pantalla diciendo
       * «No autenticado» para siempre: el portón no es una ruta, así que nada
       * devolvería al login. Con un 401 que NO sea el de la clave actual mal
       * escrita se cierra la sesión y `Marco` vuelve a pintar el login por sí
       * solo, que es lo que el usuario necesita. En la ventanita no hace falta
       * —debajo está el portal, con su botón de Salir— pero tampoco molesta. */
      if (err instanceof ApiError && err.status === 401
          && !/clave actual/i.test(err.message)) {
        void salir();
        return;
      }
      setError(err instanceof ApiError ? err.message : 'No se pudo cambiar la clave.');
    } finally {
      setEnviando(false);
    }
  }

  const campo = 'mt-1 w-full rounded-lg border border-borde bg-fondo px-3 py-2 text-sm '
    + 'outline-none focus:border-magenta';
  const rotulo = 'mt-3 block text-[11px] font-semibold uppercase tracking-wide opacity-70';

  /* Los tres campos comparten estos atributos, y no son de estilo: con el
   * interruptor de «ver» los `type` pasan a `text` y el teclado del teléfono les
   * aplicaría mayúscula inicial y autocorrección — la clave que se ve escrita no
   * sería la que se guarda. */
  const sinAyudasDelTeclado = {
    autoCapitalize: 'off',
    autoCorrect: 'off',
    spellCheck: false,
  } as const;

  const formulario = (
    <>
      {/* El corte de db/023 dicho ANTES de que escriba nada: con la clave
        * maestra el servidor devuelve 403, y enterarse después de teclear tres
        * campos es peor que no ofrecer el formulario. */}
      {suplantando ? (
        <Aviso nivel="mid" className="mt-4">
          Entraste con la <strong>clave maestra</strong> a la cuenta de{' '}
          <strong>{sesion.usuario.username}</strong>. Desde una sesión así no se puede
          cambiar la clave de otra persona: tiene que hacerlo ella misma.
        </Aviso>
      ) : listo ? (
        <Aviso nivel="ok" className="mt-4">
          <strong>Listo.</strong> Tu contraseña quedó cambiada. Úsala la próxima vez que
          entres, aquí y en la app del teléfono.
        </Aviso>
      ) : (
        <>
          {/* El usuario, oculto y de solo lectura. No es para la persona: es para
            * que el GESTOR DE CONTRASEÑAS del navegador sepa QUÉ credencial está
            * actualizando. Sin un campo `autocomplete="username"` en el
            * formulario, Chrome y Firefox guardan la clave nueva como una entrada
            * suelta sin usuario, o no ofrecen actualizar la que ya tenían — y el
            * usuario se queda con la vieja guardada y la nueva en ningún sitio. */}
          <input
            type="text"
            name="username"
            value={sesion.usuario.username}
            readOnly
            autoComplete="username"
            tabIndex={-1}
            aria-hidden="true"
            className="hidden"
          />

          <label className={rotulo} htmlFor="clave-actual">Clave actual</label>
          <input
            id="clave-actual"
            /* El interruptor de «ver» alcanza TAMBIÉN a este campo, y es el que
             * más se teclea mal: la clave que se entrega es `DH-K3M9P2QRS7`,
             * donde el 0 y la O o el 1 y la I se confunden a ciegas. Y
             * equivocarse doce veces cierra el login diez minutos, o sea la única
             * salida del portón. */
            type={ver ? 'text' : 'password'}
            value={actual}
            onChange={(e) => setActual(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
            {...sinAyudasDelTeclado}
            className={campo}
          />

          <label className={rotulo} htmlFor="clave-nueva">Clave nueva</label>
          <input
            id="clave-nueva"
            type={ver ? 'text' : 'password'}
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            autoComplete="new-password"
            aria-describedby="clave-requisitos"
            required
            {...sinAyudasDelTeclado}
            className={campo}
          />

          <label className={rotulo} htmlFor="clave-repetir">Repite la clave nueva</label>
          <input
            id="clave-repetir"
            type={ver ? 'text' : 'password'}
            value={repetir}
            onChange={(e) => setRepetir(e.target.value)}
            autoComplete="new-password"
            required
            {...sinAyudasDelTeclado}
            className={campo}
          />

          <label className="mt-3 flex items-center gap-2 text-xs opacity-70">
            <input
              type="checkbox"
              checked={ver}
              onChange={(e) => setVer(e.target.checked)}
              className="accent-magenta"
            />
            Ver lo que escribo
          </label>

          {/* La lista de requisitos, en vivo. Sale de la política compartida
            * (`lib/clave.ts`), así que no puede decir algo distinto de lo que
            * el servidor va a exigir. */}
          <ul id="clave-requisitos" className="mt-3 space-y-0.5 rounded-lg bg-suave px-3 py-2
                         text-[11px] leading-relaxed">
            {REGLAS.map((r) => {
              const cumple = r.cumple(nueva, sesion.usuario.username);
              return (
                <li
                  key={r.id}
                  className={'flex items-start gap-1.5 '
                    + (cumple ? 'text-emerald-700 dark:text-emerald-300' : 'text-texto-2')}
                >
                  <span aria-hidden className="w-3 shrink-0 text-center">
                    {cumple ? '✓' : '·'}
                  </span>
                  <span>{r.texto}</span>
                  <span className="sr-only">{cumple ? ' (cumplido)' : ' (pendiente)'}</span>
                </li>
              );
            })}
          </ul>

          {error && <Aviso nivel="bad" className="mt-3">{error}</Aviso>}

          <button
            type="submit"
            disabled={enviando}
            className="mt-4 w-full rounded-lg bg-gradient-to-r from-magenta-fondo to-magenta-fondo-2
                       px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            {enviando ? 'Guardando…' : porton ? 'Guardar y entrar' : 'Guardar mi clave'}
          </button>

          {/* La razón por la que a alguien le interesa hacer esto hoy. Solo en la
            * ventanita y solo a quien todavía no la ha cambiado: en el portón
            * sobra, porque ahí el motivo es que sin esto no se entra. */}
          {!porton && !sesion.usuario.clavePersonalizada && (
            <p className="mt-3 text-[11px] leading-relaxed text-texto-2">
              Mientras uses la clave que te entregamos, no puedes ver el{' '}
              <strong className="text-texto">recorrido guiado</strong> del portal.
              Al poner la tuya se activa.
            </p>
          )}
        </>
      )}
    </>
  );

  /* ── EL PORTÓN ─────────────────────────────────────────────────────────── */
  if (porton) {
    return (
      <div className="grid min-h-screen place-items-center bg-fondo p-4">
        <form
          onSubmit={enviar}
          className="w-full max-w-md rounded-2xl border border-borde bg-panel p-6 shadow-xl"
        >
          <h1 className="text-xl font-black tracking-tight">
            <span className="text-magenta">DH</span> Portal Master
          </h1>

          <Aviso nivel="mid" className="mt-4">
            <strong>Crea tu contraseña para entrar.</strong> Estás usando la clave que se te
            entregó. Elige una que solo tú conozcas: la vas a usar aquí y en la app del
            teléfono.
          </Aviso>

          <p className="mt-3 text-xs opacity-60">
            Cuenta: <strong>{sesion.usuario.nombre}</strong> ({sesion.usuario.username})
          </p>

          {formulario}

          {/* La salida de emergencia. Ver la cabecera: el return temprano de
            * `Marco` se lleva por delante los dos botones de Salir de la
            * aplicación. NO lleva `disabled={enviando}`, y es a propósito: si el
            * POST se queda colgado —red mala, función fría, teléfono cambiando de
            * antena— el botón se quedaría bloqueado para siempre y esta pantalla
            * no tendría NINGUNA salida. Un `fetch` sin tiempo límite y la única
            * puerta deshabilitada es la combinación que deja a alguien encerrado
            * en el portal. */}
          <button
            type="button"
            onClick={() => void salir()}
            className="mt-2 w-full rounded-lg border border-borde px-4 py-2 text-xs
                       opacity-70 hover:opacity-100"
          >
            Salir
          </button>

          <p className="mt-3 text-[11px] leading-relaxed text-texto-2">
            ¿No recuerdas la clave que te entregamos? Escríbele a Control Estadístico y te
            damos una nueva.
          </p>
        </form>
      </div>
    );
  }

  /* ── «MI CLAVE», la ventanita del botón de la cabecera ─────────────────── */
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && onCerrar) onCerrar(); }}
    >
      <form
        onSubmit={enviar}
        role="dialog"
        aria-modal="true"
        aria-labelledby="clave-titulo"
        className="w-full max-w-sm rounded-2xl border border-borde bg-panel p-5 shadow-2xl"
      >
        <div className="flex items-start">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-40">
              Tu cuenta
            </p>
            <h2 id="clave-titulo" className="text-base font-black leading-tight">
              Cambiar mi contraseña
            </h2>
            <p className="mt-0.5 text-xs opacity-60">{sesion.usuario.username}</p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="ml-auto rounded-lg px-2 py-1 text-sm opacity-55 hover:bg-suave hover:opacity-100"
          >
            ✕
          </button>
        </div>

        {formulario}
      </form>
    </div>
  );
}
